import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  EntitatiSilviceListResponse,
  EntitateSilvicaDto,
  EntitateSilvicaUpdateInput,
  OcoaleListResponse,
  OcolSilvicDto,
  OcolSilvicCreateInput,
  OcolSilvicUpdateInput,
  PrimariiListResponse,
  PrimarieDto,
  PrimarieCreateInput,
  PrimarieUpdateInput,
  ProjectTypeDto,
  ProjectTypeCreateInput,
  ProjectTypeListResponse,
  ProjectTypeUpdateInput,
  TreeSpeciesDto,
  TreeSpeciesCreateInput,
  TreeSpeciesListResponse,
  TreeSpeciesUpdateInput,
  WorkTypeDto,
  WorkTypeCreateInput,
  WorkTypeListResponse,
  WorkTypeUpdateInput,
} from '@forest/contracts';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';

const STALE_5_MIN = 5 * 60_000;

// ----------------------------------------------------------------
// Reads
// ----------------------------------------------------------------

/** All entitati silvice with nested ocoale. Cheap (~26 rows). */
export function useEntitatiSilvice() {
  return useQuery<EntitatiSilviceListResponse>({
    queryKey: ['regions', 'entitati-silvice'],
    queryFn: () =>
      api.get('regions/entitati-silvice').json<EntitatiSilviceListResponse>(),
    staleTime: STALE_5_MIN,
  });
}

/** All ocoale silvice (~87 rows when fully imported). */
export function useOcoaleSilvice() {
  return useQuery<OcoaleListResponse>({
    queryKey: ['regions', 'ocoale-silvice', 'all'],
    queryFn: () =>
      api.get('regions/ocoale-silvice?limit=500').json<OcoaleListResponse>(),
    staleTime: STALE_5_MIN,
  });
}

/** Tree-species reference codes (Specia de Bază). Cheap (~69 rows). */
export function useTreeSpecies() {
  return useQuery<TreeSpeciesListResponse>({
    queryKey: ['regions', 'tree-species'],
    queryFn: () =>
      api.get('regions/tree-species').json<TreeSpeciesListResponse>(),
    staleTime: STALE_5_MIN,
  });
}

/** Work-type reference texts (ultima lucrare executată). Cheap (~124 rows). */
export function useWorkTypes() {
  return useQuery<WorkTypeListResponse>({
    queryKey: ['regions', 'work-types'],
    queryFn: () => api.get('regions/work-types').json<WorkTypeListResponse>(),
    staleTime: STALE_5_MIN,
  });
}

/**
 * Photo "project" reference rows (dynamic ProjectType table, replaces the
 * former hardcoded `PhotoProject` enum). Ordered server-side by
 * `sortOrder` then `name`. Cheap (~6 rows). Drives every project <select>
 * across the photo surfaces.
 */
export function useProjectTypes() {
  return useQuery<ProjectTypeListResponse>({
    queryKey: ['regions', 'project-types'],
    queryFn: () =>
      api.get('regions/project-types').json<ProjectTypeListResponse>(),
    staleTime: STALE_5_MIN,
  });
}

/**
 * Primarii — large (~900). Pulled in chunks of 200 since the page-1
 * default the route returns is 50; for combobox suggestions we want the
 * full set in cache so the datalist filters client-side.
 */
export function useAllPrimarii() {
  return useQuery<PrimariiListResponse>({
    queryKey: ['regions', 'primarii', 'all'],
    queryFn: async () => {
      const collected: PrimariiListResponse['items'] = [];
      let page = 1;
      // Hard-cap to keep the worst case bounded.
      while (page <= 10) {
        const res = await api
          .get(`regions/primarii?page=${page}&limit=200`)
          .json<PrimariiListResponse>();
        collected.push(...res.items);
        if (res.items.length < res.pagination.limit) break;
        page += 1;
      }
      return {
        items: collected,
        pagination: { page: 1, limit: collected.length, total: collected.length, totalPages: 1 },
      };
    },
    staleTime: STALE_5_MIN,
  });
}

// ----------------------------------------------------------------
// Mutations (SUPER_ADMIN inline rename of reference rows)
//
// Each PATCH returns the updated DTO; on success we invalidate the
// whole `['regions']` tree so every dependent list (state panel,
// editable section, combobox caches) re-reads. On error we surface
// the server message via the toast — the caller (input) re-renders
// against the freshly-refetched server value so a rejected edit
// (e.g. a 409 unique conflict) visibly reverts.
// ----------------------------------------------------------------

/** Shared error handler: extracts the API error envelope and toasts it. */
function useRegionsMutationToasts() {
  const { t } = useTranslation(['admin', 'common']);
  const pushToast = useUiStore((s) => s.pushToast);
  const onError = async (e: unknown) => {
    const err = await apiError(e);
    pushToast({ variant: 'error', message: err.message });
  };
  return { t, pushToast, onError };
}

export function useUpdatePrimarie() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PrimarieUpdateInput }) =>
      api.patch(`regions/primarii/${id}`, { json: data }).json<PrimarieDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

export function useUpdateEntitateSilvica() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EntitateSilvicaUpdateInput }) =>
      api
        .patch(`regions/entitati-silvice/${id}`, { json: data })
        .json<EntitateSilvicaDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

export function useCreateEntitateSilvica() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: (data: { cod: string; name: string }) =>
      api
        .post('regions/entitati-silvice', { json: data })
        .json<EntitateSilvicaDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

export function useUpdateOcolSilvic() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OcolSilvicUpdateInput }) =>
      api
        .patch(`regions/ocoale-silvice/${id}`, { json: data })
        .json<OcolSilvicDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

export function useUpdateTreeSpecies() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TreeSpeciesUpdateInput }) =>
      api
        .patch(`regions/tree-species/${id}`, { json: data })
        .json<TreeSpeciesDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

export function useUpdateWorkType() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: WorkTypeUpdateInput }) =>
      api.patch(`regions/work-types/${id}`, { json: data }).json<WorkTypeDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

export function useUpdateProjectType() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProjectTypeUpdateInput }) =>
      api
        .patch(`regions/project-types/${id}`, { json: data })
        .json<ProjectTypeDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

// ----------------------------------------------------------------
// Create mutations (SUPER_ADMIN ad-hoc insert of a single reference row,
// without re-importing regions.xlsx).
//
// Each POST returns the freshly-created DTO. On success we invalidate the
// whole `['regions']` tree so every dependent list (state panel, editable
// section, combobox caches) re-reads and the new row appears. On error
// (e.g. a 409 unique-key conflict) `onError` surfaces the server message
// via the toast; the caller keeps the form filled so the user can correct
// and retry. Mirrors `AddEntitateSilvicaForm`'s mutation shape.
// ----------------------------------------------------------------

export function useCreatePrimarie() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: (data: PrimarieCreateInput) =>
      api.post('regions/primarii', { json: data }).json<PrimarieDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

export function useCreateOcolSilvic() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: (data: OcolSilvicCreateInput) =>
      api.post('regions/ocoale-silvice', { json: data }).json<OcolSilvicDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

export function useCreateTreeSpecies() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: (data: TreeSpeciesCreateInput) =>
      api.post('regions/tree-species', { json: data }).json<TreeSpeciesDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

export function useCreateWorkType() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: (data: WorkTypeCreateInput) =>
      api.post('regions/work-types', { json: data }).json<WorkTypeDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}

export function useCreateProjectType() {
  const qc = useQueryClient();
  const { onError } = useRegionsMutationToasts();
  return useMutation({
    mutationFn: (data: ProjectTypeCreateInput) =>
      api.post('regions/project-types', { json: data }).json<ProjectTypeDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regions'] });
    },
    onError,
  });
}
